import type { AddressBook } from '../contracts/address-book.contract';

import {
    ChangeDetectionStrategy,
    Component,
    booleanAttribute,
    effect,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { FormField, form, required } from '@angular/forms/signals';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'sp-address-books-ui',
    templateUrl: './address-books-ui.component.html',
    styleUrls: ['./address-books-ui.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormField, TranslateModule],
})
export class AddressBooksUiComponent {
    /** Currently selected address book id; `null` when nothing is picked yet. */
    public readonly selected = input.required<number | null>();
    public readonly selectedChange = output<number>();

    public readonly data = input.required<AddressBook[]>();
    public readonly isLoading = input.required<boolean>();
    public readonly isError = input.required<boolean>();

    public readonly showContactHint = input(false, { transform: booleanAttribute });

    /**
     * Internal one-field signal form driving the select via `[formField]`.
     * Native selects speak strings, so the field is string-typed ('' = nothing
     * selected); the number conversion happens at the input/output boundary.
     */
    private readonly model = signal('');
    protected readonly bookForm = form(this.model, (path) => {
        required(path);
    });

    protected isSelected(id: number): boolean {
        return this.bookForm().value() === String(id);
    }

    constructor() {
        // input → form (host value, undo/redo, external resets)
        effect(() => {
            const selected = this.selected();
            this.model.set(selected === null ? '' : String(selected));
        });

        // form → output: emits only when the form value diverges from the
        // input, i.e. on user picks through the select — the input → form
        // sync above converges them again, so no echo loop.
        effect(() => {
            const raw = this.bookForm().value();
            if (raw === '') return;

            const next = Number(raw);
            if (next !== untracked(this.selected)) {
                this.selectedChange.emit(next);
            }
        });
    }
}
